import type {ChangeEvent, InputHTMLAttributes, ReactNode} from 'react';

import {createContext, useContext, useId, useState} from 'react';

import {cn} from '../../helpers/cn';

export type RadioChangeEvent = {
  target: {
    value: string;
    checked: boolean;
  };
  stopPropagation: () => void;
  preventDefault: () => void;
};

type RadioGroupContextValue = {
  name: string;
  value?: string;
  disabled?: boolean;
  onChange?: (e: RadioChangeEvent) => void;
};

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null);

export type RadioProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'onChange' | 'size'
> & {
  children?: ReactNode;
  onChange?: (e: RadioChangeEvent) => void;
};

export function Radio({
  value,
  disabled,
  children,
  className,
  id,
  name,
  checked,
  defaultChecked,
  onChange,
  ...props
}: RadioProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const group = useContext(RadioGroupContext);

  const isDisabled = disabled ?? group?.disabled;
  const isGroupControlled = group != null && group.value !== undefined;
  const isChecked = isGroupControlled ? String(group.value) === String(value) : checked;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const radioEvent: RadioChangeEvent = {
      target: {
        value: event.target.value,
        checked: event.target.checked
      },
      stopPropagation: () => event.stopPropagation(),
      preventDefault: () => event.preventDefault()
    };
    group?.onChange?.(radioEvent);
    onChange?.(radioEvent);
  };

  return (
    <label
      htmlFor={inputId}
      className={cn(
        'ds-radio inline-flex cursor-pointer items-center gap-2 font-iransans text-body-sm text-neutral-fg [--ds-wave-color:var(--color-orange)]',
        isDisabled && 'cursor-not-allowed text-disabled-fg',
        className
      )}
    >
      {/* Ant Design 5 non-wireframe: checked = solid primary fill + white center dot */}
      <span className="relative inline-flex size-4 shrink-0 items-center justify-center overflow-visible">
        <input
          {...props}
          id={inputId}
          type="radio"
          name={group?.name ?? name}
          value={value}
          checked={isGroupControlled || checked !== undefined ? isChecked : undefined}
          defaultChecked={!isGroupControlled && checked === undefined ? defaultChecked : undefined}
          disabled={isDisabled}
          onChange={handleChange}
          className="ds-radio-input peer absolute inset-0 z-10 m-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
        <span
          aria-hidden
          className={cn(
            'ds-radio-inner ds-wave-quick pointer-events-none relative flex size-4 items-center justify-center overflow-visible rounded-full border border-disabled-border bg-white',
            'peer-checked:border-orange peer-checked:bg-orange',
            'peer-checked:[&>span]:opacity-100 peer-checked:[&>span]:scale-[0.375]',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-orange',
            'peer-hover:border-orange',
            'peer-disabled:border-disabled-border peer-disabled:bg-disabled-surface',
            'peer-disabled:peer-checked:[&>span]:bg-disabled-fg'
          )}
        >
          {/* Full-size then scaled — same technique as antd ::after */}
          <span className="absolute -inset-px block size-4 scale-0 rounded-full bg-white opacity-0 transition-all duration-300 ease-[cubic-bezier(0.78,0.14,0.15,0.86)]" />
        </span>
      </span>
      {children != null && <span className="min-w-0 leading-5">{children}</span>}
    </label>
  );
}

export type RadioGroupProps = {
  name?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  children?: ReactNode;
  className?: string;
  onChange?: (e: RadioChangeEvent) => void;
};

export function RadioGroup({
  name,
  value,
  defaultValue,
  disabled,
  children,
  className,
  onChange
}: RadioGroupProps) {
  const generatedName = useId();
  const groupName = name ?? generatedName;
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const currentValue = isControlled ? value : uncontrolledValue;

  const handleChange = (e: RadioChangeEvent) => {
    if (!isControlled) {
      setUncontrolledValue(e.target.value);
    }
    onChange?.(e);
  };

  return (
    <RadioGroupContext.Provider
      value={{
        name: groupName,
        value: currentValue,
        disabled,
        onChange: handleChange
      }}
    >
      <div role="radiogroup" className={cn('flex flex-col', className)}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

Radio.Group = RadioGroup;
